import { intlLocale } from './i18nText';
/**
 * chatFormat.ts — small pure helpers for how chat reads: grouping runs of
 * messages, day separators, room-list previews and times. Locale-aware via Intl.
 */

/** Messages from the same sender within this window share one header */
export const GROUP_WINDOW_MS = 5 * 60 * 1000;

interface MsgLike {
  senderId: string;
  createdAt: string;
  type?: string;
  isDeleted?: boolean;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export function isNewDay(prev: MsgLike | undefined, msg: MsgLike): boolean {
  if (!prev) return true;
  return startOfDay(new Date(prev.createdAt)) !== startOfDay(new Date(msg.createdAt));
}

/** True when `msg` continues `prev`'s run: same sender, same day, within 5 minutes. */
export function isGroupedWith(prev: MsgLike | undefined, msg: MsgLike): boolean {
  if (!prev) return false;
  if (prev.senderId !== msg.senderId) return false;
  if (prev.type === 'call_invite' || msg.type === 'call_invite' || prev.type === 'system' || msg.type === 'system') return false;
  if (isNewDay(prev, msg)) return false;
  const gap = new Date(msg.createdAt).getTime() - new Date(prev.createdAt).getTime();
  return gap >= 0 && gap < GROUP_WINDOW_MS;
}

/**
 * Label for a day separator. Returns { key } for "today"/"yesterday" so the
 * caller can translate, otherwise a formatted date in the given locale.
 */

export function dayLabel(iso: string, lng: string, now = new Date()): { key: 'today' | 'yesterday' } | { text: string } {
  const d = new Date(iso);
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return { key: 'today' };
  if (diffDays === 1) return { key: 'yesterday' };
  const sameYear = d.getFullYear() === now.getFullYear();
  return {
    text: new Intl.DateTimeFormat(intlLocale(lng), diffDays < 7
      ? { weekday: 'long', day: 'numeric', month: 'long' }
      : { day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }) }).format(d),
  };
}

/** Compact time for the room list: 09:14 today, "Tue" this week, "12 Oct" before that. */
export function roomTimeLabel(iso: string | undefined, lng: string, now = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays <= 0) return new Intl.DateTimeFormat(intlLocale(lng), { hour: '2-digit', minute: '2-digit' }).format(d);
  if (diffDays < 7) return new Intl.DateTimeFormat(intlLocale(lng), { weekday: 'short' }).format(d);
  return new Intl.DateTimeFormat(intlLocale(lng), { day: 'numeric', month: 'short', ...(d.getFullYear() === now.getFullYear() ? {} : { year: '2-digit' }) }).format(d);
}

const ENTITIES: Record<string, string> = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&nbsp;': ' ' };

/** One-line plain-text preview of a message body (HTML or markdown). */
export function previewText(content: string | null | undefined, max = 80): string {
  if (!content) return '';
  let s = content
    .replace(/<br\s*\/?>|<\/p>|<\/li>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, m => ENTITIES[m] ?? m)
    .replace(/```[\s\S]*?```/g, '[code]')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '[image]')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/(^|\s)[#>*-]+\s+/g, '$1')
    .replace(/[*_~`]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (s.length > max) s = s.slice(0, max - 1).trimEnd() + '…';
  return s;
}

interface RoomLike { type: string; lastMessage?: { at: string } | null }

/** Newest activity first; rooms without messages keep their relative order at the end. */
export function byRecency<T extends RoomLike>(rooms: T[]): T[] {
  return rooms
    .map((r, i) => ({ r, i, t: r.lastMessage?.at ? new Date(r.lastMessage.at).getTime() : -Infinity }))
    .sort((a, b) => (b.t - a.t) || (a.i - b.i))
    .map(x => x.r);
}
